import { isDevelopment } from './environment.utils';

function hash(str) {
    let h = 0,
        i = 0;
    for (; i < str.length; i++) {
        h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
    }
    return h;
}

function injectScript(url, originalId, onLoad, crossorigin) {
    let id = originalId;
    if (!id) {
        id = 'hbid' + hash(url);
    }

    if (document.getElementById(id)) {
        onLoad();
        return false;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = url;
    script.async = true;
    script.defer = true;
    script.id = id;
    script.onload = onLoad;
    if (crossorigin) {
        script.crossOrigin = crossorigin;
    }

    document.head.appendChild(script);
    return true;
}

function loopUntilReady(getObject, resolve) {
    setTimeout(() => {
        const obj = getObject();
        if (obj) {
            resolve(obj);
        } else {
            loopUntilReady(getObject, resolve);
        }
    }, 200);
}

export function waitForWindowProperty(property) {
    return new Promise(resolve => {
        loopUntilReady(() => window[property], resolve);
    });
}

export function loadCallbackPromise<T>(
    callback: () => void,
    getObject: () => T
): Promise<T> {
    return new Promise<T>(resolve => {
        const obj = getObject();
        if (obj) {
            resolve(obj);
        } else {
            callback();
            loopUntilReady(getObject, resolve);
        }
    });
}

export type TCrossOrigin = 'anonymous' | 'use-credentials' | null;

export function loadUrlPromise<T>(
    url: string,
    getObject: () => T,
    id: string | null = null,
    crossorigin: TCrossOrigin = null
): Promise<T> {
    return new Promise<T>(resolve => {
        if (
            !injectScript(
                url,
                id,
                () => loopUntilReady(getObject, resolve),
                crossorigin
            )
        ) {
            resolve(getObject());
        }
    });
}

export function loadCss(url) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.type = 'text/css';
    link.href = url;
    document.head.appendChild(link);
}

export function injectMeta(attrs: { [key: string]: string }) {
    const meta = document.createElement('meta');
    Object.entries(attrs).forEach(([key, value]) =>
        meta.setAttribute(key, value)
    );
    document.head.appendChild(meta);
}

export function loadScript(options: Record<string, string | boolean>) {
    const script = document.createElement('script');
    Object.entries(options).forEach(([key, value]) => {
        if (key === 'textContent') {
            script.textContent = String(value);
        } else if (typeof value === 'boolean') {
            if (value) {
                script.setAttribute(key, '');
            }
        } else {
            script.setAttribute(key, value);
        }
    });
    document.head.appendChild(script);
}

export function loadOneTrustScripts() {
    // on dev env, the script causes a wires crossed error after string change in a json file. https://honeybook.atlassian.net/browse/RF-143
    if (isDevelopment()) {
        return;
    }
    const scriptId = window.isProduction
        ? '31c6c3b7-6b4c-4bdd-8905-336e2068baeb'
        : '31c6c3b7-6b4c-4bdd-8905-336e2068baeb-test';
    loadScript({
        type: 'text/javascript',
        src: `https://cdn.cookielaw.org/consent/${scriptId}/OtAutoBlock.js`
    });
    loadScript({
        type: 'text/javascript',
        charset: 'UTF-8',
        'data-domain-script': scriptId,
        src: 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js'
    });
    loadScript({
        type: 'text/javascript',
        textContent: 'function OptanonWrapper() {}'
    });
}

export function loadMTAPixel() {
    loadScript({
        type: 'text/javascript',
        textContent:
            'var rf = encodeURIComponent(window.location); var img = new Image(); img.src = "' +
            (process.env.NODE_ENV === 'production'
                ? 'https://pixel.honeybook.com/mta'
                : 'https://pixel-dev.honeybook.com/mta') +
            '?sk=1"+ (rf ? "&rf="+rf : "" );'
    });
}

export function showIntercomChatIfRequested() {
    const urlParams = new URLSearchParams(window.location.search);
    const shouldShowChat = urlParams.get('chat') === 'show';

    if (shouldShowChat && window.Intercom) {
        window.Intercom('show');
    }
}
